import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";

const resolveBlogCopy = (post, t, getContent) => {
  const fallback = t.blogPosts?.[post.slug] || {};
  return {
    ...post,
    title: getContent(`blog.${post.slug}.title`, "text", fallback.title || post.title || ""),
    excerpt: getContent(`blog.${post.slug}.excerpt`, "text", fallback.excerpt || post.excerpt || ""),
    body: getContent(`blog.${post.slug}.body`, "text", fallback.body || post.body || ""),
  };
};

export function BlogList() {
  const { t, getContent } = useApp();
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);

  if (posts.length === 0) return (
    <main className="pt-32 pb-32 bg-[#f6f6f3]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="overline mb-4">{t.home.journal}</div>
        <h1 className="font-serif-display text-6xl md:text-8xl text-[#0b111e]">{t.home.storiesGuides}</h1>
        <p className="text-[#0b111e]/60 mt-8 text-lg max-w-md">Nenhum post disponível no momento. Volte em breve para ver as últimas histórias e guias da SharkX.</p>
      </div>
    </main>
  );

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3]" data-testid="blog-list">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="overline mb-4">{t.home.journal}</div>
        <h1 className="font-serif-display text-6xl md:text-8xl text-[#0b111e]">{t.home.storiesGuides}</h1>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((b) => {
            const post = resolveBlogCopy(b, t, getContent);
            return (
              <Link key={b.id} to={`/blog/${b.slug}`} className="group" data-testid={`post-${b.slug}`}>
                <div className="aspect-[4/3] overflow-hidden mb-5">
                  <img src={b.cover} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="overline mb-2 text-[#0b111e]/60">{b.author}</div>
                <div className="font-serif-display text-2xl md:text-3xl leading-tight group-hover:text-sky-500 transition-colors text-[#0b111e]">{post.title}</div>
                <div className="text-[#0b111e]/60 mt-3 text-sm">{post.excerpt}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
}

export function BlogPost() {
  const { t, getContent } = useApp();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => { if (!post) setError(true); }, 6000);
    axios.get(`${API}/blog/${slug}`).then((r) => { setPost(r.data); clearTimeout(timer); }).catch(() => { setError(true); clearTimeout(timer); });
    return () => clearTimeout(timer);
  }, [slug]);

  if (error && !post) return (
    <main className="pt-40 pb-32 text-center bg-[#f6f6f3]">
      <div className="max-w-xl mx-auto px-6">
        <div className="overline mb-4 text-[#0b111e]/40">Offline</div>
        <h1 className="font-serif-display text-4xl text-[#0b111e] mb-4">{t.home.loading || "Conteúdo indisponível"}</h1>
        <p className="text-[#0b111e]/60 mb-8">Não foi possível carregar este post agora. Isso pode acontecer quando o servidor está temporariamente offline.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/blog" className="text-sm uppercase tracking-widest text-sky-500 hover:text-sky-700 font-ui">← Voltar ao Blog</Link>
          <Link to="/contact" className="text-sm uppercase tracking-widest text-sky-500 hover:text-sky-700 font-ui">Falar com a SharkX →</Link>
        </div>
      </div>
    </main>
  );

  if (!post) return <div className="pt-40 text-center text-[#0b111e]/60">{t.home.loading || "Loading…"}</div>;
  const localizedPost = resolveBlogCopy(post, t, getContent);
  return (
    <main className="pb-32 bg-[#f6f6f3]" data-testid="blog-post">
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0">
          <img src={post.cover} alt="" aria-hidden="true" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20" />
        </div>
        <div className="relative mx-auto flex min-h-[64vh] max-w-6xl items-end px-6 py-24 md:px-12 md:py-32">
          <div className="max-w-3xl pb-10">
            <div className="overline mb-4 text-white/70">{post.author}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl text-white">{localizedPost.title}</h1>
            <p className="mt-5 max-w-2xl text-lg md:text-xl leading-relaxed text-white/85">{localizedPost.excerpt}</p>
          </div>
        </div>
      </section>
      <section className="relative -mt-8">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/80 bg-white/95 p-8 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur md:p-12">
            <div className="text-[#0b111e]/80 leading-relaxed whitespace-pre-line text-lg md:text-xl">{localizedPost.body}</div>
            <Link to="/blog" className="inline-block mt-12 text-xs uppercase tracking-[0.2em] font-ui text-[#0b111e]/60 hover:text-sky-500">← {t.home.storiesGuides}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
